import Phaser from "phaser";
import type { EconomyOutpostSummary } from "../../../../shared/types/BattleEvent";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";
import mapConfig from "../data/mapConfig.json";
import { Unit } from "../entities/Unit";

type OutpostOwner = "neutral" | "player" | "enemy";

type EconomyOutpost = {
  lane: LaneNumber;
  x: number;
  y: number;
  owner: OutpostOwner;
  progress: number;
  marker: Phaser.GameObjects.Container;
  ring: Phaser.GameObjects.Arc;
  flag: Phaser.GameObjects.Text;
  progressFill: Phaser.GameObjects.Rectangle;
  playerUnits: number;
  enemyUnits: number;
};

export type EconomyOutpostCallbacks = {
  onCaptured?: (summary: EconomyOutpostSummary) => void;
};

const CAPTURE_MS = 10000;
const CAPTURE_RADIUS = 74;

export class EconomyOutpostSystem {
  private readonly outposts: EconomyOutpost[] = [];
  private lastUpdateAt = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly callbacks: EconomyOutpostCallbacks = {}) {
    for (const lane of mapConfig.lanes) {
      const x = this.outpostX(lane.lane as LaneNumber);
      const marker = this.createMarker(x, lane.y);
      this.outposts.push({
        lane: lane.lane as LaneNumber,
        x,
        y: lane.y,
        owner: "neutral",
        progress: 0,
        marker: marker.container,
        ring: marker.ring,
        flag: marker.flag,
        progressFill: marker.progressFill,
        playerUnits: 0,
        enemyUnits: 0
      });
    }
  }

  update(time: number, playerUnits: Unit[], enemies: Unit[]) {
    const elapsedMs = Math.min(250, Math.max(0, time - this.lastUpdateAt));
    this.lastUpdateAt = time;

    for (const outpost of this.outposts) {
      const previousOwner = outpost.owner;
      outpost.playerUnits = this.unitsInOutpost(outpost, playerUnits);
      outpost.enemyUnits = this.unitsInOutpost(outpost, enemies);
      const pressure = Math.sign(outpost.playerUnits - outpost.enemyUnits);

      if (pressure > 0) {
        outpost.progress = Math.min(CAPTURE_MS, outpost.progress + elapsedMs * Math.min(1.8, 1 + (outpost.playerUnits - 1) * 0.22));
      } else if (pressure < 0) {
        outpost.progress = Math.max(-CAPTURE_MS, outpost.progress - elapsedMs * Math.min(1.8, 1 + (outpost.enemyUnits - 1) * 0.22));
      } else {
        outpost.progress *= 0.992;
        if (Math.abs(outpost.progress) < 12) outpost.progress = 0;
      }

      if (outpost.progress >= CAPTURE_MS) {
        outpost.owner = "player";
      } else if (outpost.progress <= -CAPTURE_MS) {
        outpost.owner = "enemy";
      } else if (Math.abs(outpost.progress) < CAPTURE_MS * 0.18) {
        outpost.owner = "neutral";
      }

      this.updateMarker(outpost);
      if (outpost.owner !== previousOwner && outpost.owner !== "neutral") {
        this.callbacks.onCaptured?.(this.summaryFor(outpost));
      }
    }
  }

  get playerOwnedCount() {
    return this.outposts.filter((outpost) => outpost.owner === "player").length;
  }

  get enemyOwnedCount() {
    return this.outposts.filter((outpost) => outpost.owner === "enemy").length;
  }

  get summaries(): EconomyOutpostSummary[] {
    return this.outposts.map((outpost) => this.summaryFor(outpost));
  }

  private unitsInOutpost(outpost: EconomyOutpost, units: Unit[]) {
    return units.filter((unit) => unit.alive && unit.lane === outpost.lane && Math.abs(unit.x - outpost.x) <= CAPTURE_RADIUS).length;
  }

  private outpostX(lane: LaneNumber) {
    if (lane === 1) return 610;
    if (lane === 2) return 590;
    if (lane === 3) return 640;
    if (lane === 4) return 640;
    return 610;
  }

  private createMarker(x: number, y: number) {
    const container = this.scene.add.container(x, y + 34).setDepth(38);
    const shadow = this.scene.add.ellipse(0, 10, 72, 22, 0x030305, 0.32);
    const ring = this.scene.add.circle(0, 0, 26).setStrokeStyle(2, 0xd2c37d, 0.66);
    const flag = this.scene.add
      .text(0, -8, "$", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "18px",
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setResolution(2)
      .setOrigin(0.5);
    const progressBack = this.scene.add.rectangle(0, 19, 52, 5, 0x07080a, 0.82);
    const progressFill = this.scene.add.rectangle(0, 19, 1, 4, 0xd2c37d, 0.92).setOrigin(0.5);
    container.add([shadow, ring, flag, progressBack, progressFill]);
    return { container, ring, flag, progressFill };
  }

  private updateMarker(outpost: EconomyOutpost) {
    const color = outpost.owner === "player" ? 0x70d7ff : outpost.owner === "enemy" ? 0xff604f : 0xd2c37d;
    const progressColor = outpost.progress > 0 ? 0x70d7ff : outpost.progress < 0 ? 0xff604f : 0xd2c37d;
    outpost.ring.setStrokeStyle(2, color, outpost.owner === "neutral" ? 0.66 : 0.95);
    outpost.flag.setColor(`#${color.toString(16).padStart(6, "0")}`);
    outpost.progressFill.setFillStyle(progressColor, 0.94);
    outpost.progressFill.width = Math.max(1, 52 * Math.min(1, Math.abs(outpost.progress) / CAPTURE_MS));
  }

  private summaryFor(outpost: EconomyOutpost): EconomyOutpostSummary {
    return {
      lane: outpost.lane,
      owner: outpost.owner,
      progress: Number((outpost.progress / CAPTURE_MS).toFixed(2)),
      playerUnits: outpost.playerUnits,
      enemyUnits: outpost.enemyUnits
    };
  }
}
